import React from 'react'
import { X, Printer } from 'lucide-react'
import './PrintFinance.css'

const PrintBudget = ({ budget, onClose }) => {
  if (!budget) return null

  const formatDate = (date) => {
    if (!date) return 'N/A'
    return new Date(date).toLocaleDateString('en-KE', {
      day: '2-digit', month: 'long', year: 'numeric'
    })
  }

  const formatCurrency = (amount) => {
    return `KES ${(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
  }

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Budget - ${budget.budgetName || 'N/A'}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; 
          font-size: 13px; 
          line-height: 1.5; 
          color: #333;
          background: #f5f5f5;
          padding: 20px;
        }
        .print-page {
          width: 794px;
          min-height: 1123px;
          margin: 0 auto;
          background: white;
          padding: 40px;
          box-shadow: 0 0 20px rgba(0,0,0,0.1);
        }
        .print-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 3px solid #1a237e;
        }
        .print-logo-section {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        .print-logo {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #1a237e 100%);
          border-radius: 50%;
          border: 3px solid #ffd700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
          box-shadow: 0 2px 8px rgba(26, 35, 126, 0.2);
          flex-shrink: 0;
        }
        .print-logo-g, .print-logo-s {
          font-size: 24px;
          font-weight: 900;
          color: #ffd700;
          text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
        }
        .print-logo-shield { font-size: 18px; }
        .print-company h1 {
          font-size: 20px;
          font-weight: 700;
          color: #1a237e;
          margin: 0;
        }
        .print-company p {
          font-size: 12px;
          color: #666;
          margin: 4px 0;
        }
        .print-doc-info {
          text-align: right;
        }
        .print-doc-type {
          display: inline-block;
          background: #1a237e;
          color: #ffd700;
          padding: 8px 20px;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          border-radius: 4px;
          margin-bottom: 10px;
        }
        .print-doc-id {
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }
        .print-doc-date {
          font-size: 13px;
          color: #666;
          margin-top: 6px;
        }
        .print-body {
          margin: 30px 0;
        }
        .print-section {
          margin-bottom: 25px;
        }
        .print-section h3 {
          font-size: 14px;
          font-weight: 700;
          color: #1a237e;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 2px solid #e8eaf6;
        }
        .print-detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .print-detail-item {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #f0f0f0;
        }
        .print-detail-item label {
          font-weight: 600;
          color: #666;
          font-size: 12px;
          text-transform: uppercase;
        }
        .print-detail-item span {
          font-weight: 500;
          color: #333;
        }
        .print-amount {
          font-size: 24px;
          font-weight: 700;
          color: #1a237e;
          text-align: right;
          padding: 20px;
          background: #e8eaf6;
          border-radius: 8px;
          margin: 20px 0;
        }
        .print-footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 2px solid #e0e0e0;
        }
        .print-footer-line {
          width: 100%;
          height: 3px;
          background: linear-gradient(90deg, #1a237e, #ffd700, #1a237e);
          margin-bottom: 15px;
        }
        .print-footer-text {
          font-size: 11px;
          color: #666;
          text-align: center;
          line-height: 1.6;
        }
        .print-footer-thanks {
          font-size: 14px;
          font-weight: 600;
          color: #1a237e;
          text-align: center;
          margin: 15px 0;
          font-style: italic;
        }
        .print-signature {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          margin-top: 30px;
        }
        .print-sig-box {
          text-align: center;
        }
        .print-sig-line {
          width: 100%;
          height: 1px;
          background: #333;
          margin: 40px 0 10px;
        }
        .print-sig-label {
          font-size: 11px;
          color: #666;
          font-weight: 600;
        }
        @media print {
          body { background: white; padding: 0; }
          .print-page { box-shadow: none; width: 100%; padding: 0; }
          .no-print { display: none !important; }
        }
      </style>
    </head>
    <body>
      <div class="print-page">
        <div class="print-header">
          <div class="print-logo-section">
            <div class="print-logo">
              <span class="print-logo-g">G</span>
              <span class="print-logo-shield">🛡</span>
              <span class="print-logo-s">S</span>
            </div>
            <div class="print-company">
              <h1>Gismac Security Limited</h1>
              <p>Email: Finance@gismac.co.ke</p>
              <p>www.secs.gismac.co.ke | Nairobi, Kenya</p>
            </div>
          </div>
          <div class="print-doc-info">
            <div class="print-doc-type">Budget</div>
            <div class="print-doc-id">Ref: ${budget._id || 'N/A'}</div>
            <div class="print-doc-date">${formatDate(budget.createdAt)}</div>
          </div>
        </div>

        <div class="print-body">
          <div class="print-section">
            <h3>Budget Information</h3>
            <div class="print-detail-grid">
              <div class="print-detail-item">
                <label>Budget Name</label>
                <span>${budget.budgetName || 'N/A'}</span>
              </div>
              <div class="print-detail-item">
                <label>Department</label>
                <span>${budget.department || 'N/A'}</span>
              </div>
              <div class="print-detail-item">
                <label>Fiscal Year</label>
                <span>${budget.fiscalYear || 'N/A'}</span>
              </div>
              <div class="print-detail-item">
                <label>Period</label>
                <span>${budget.period || 'N/A'}</span>
              </div>
              <div class="print-detail-item">
                <label>Status</label>
                <span>${budget.status || 'N/A'}</span>
              </div>
              <div class="print-detail-item">
                <label>Created By</label>
                <span>${budget.createdBy?.firstName || 'N/A'} ${budget.createdBy?.lastName || ''}</span>
              </div>
            </div>
          </div>

          <div class="print-section">
            <h3>Financial Summary</h3>
            <div class="print-amount">
              Total Budget: ${formatCurrency(budget.totalAmount || budget.amount || 0)}
            </div>
            <div class="print-detail-grid">
              <div class="print-detail-item">
                <label>Allocated Amount</label>
                <span>${formatCurrency(budget.allocatedAmount || 0)}</span>
              </div>
              <div class="print-detail-item">
                <label>Spent Amount</label>
                <span>${formatCurrency(budget.spentAmount || 0)}</span>
              </div>
              <div class="print-detail-item">
                <label>Remaining</label>
                <span style="color: ${(budget.remainingAmount || 0) < 0 ? '#c62828' : '#2e7d32'}; font-weight: 700;">
                  ${formatCurrency(budget.remainingAmount || 0)}
                </span>
              </div>
              <div class="print-detail-item">
                <label>Utilization</label>
                <span>${budget.utilization || '0'}%</span>
              </div>
            </div>
          </div>

          ${budget.description ? `
          <div class="print-section">
            <h3>Description</h3>
            <p style="font-size: 13px; color: #555; line-height: 1.6;">${budget.description}</p>
          </div>
          ` : ''}
        </div>

        <div class="print-footer">
          <div class="print-footer-line"></div>
          <div class="print-footer-thanks">Thank you for your business!</div>
          <div class="print-footer-text">
            <p><strong>Payment Terms:</strong> Payment due within 30 days. Late payments subject to 1.5% monthly interest.</p>
            <p><strong>Bank:</strong> Equity Bank Kenya | Account: Gismac Security Ltd | Swift: EQBLKENA</p>
          </div>
          <div class="print-signature">
            <div class="print-sig-box">
              <div class="print-sig-line"></div>
              <div class="print-sig-label">Prepared By</div>
            </div>
            <div class="print-sig-box">
              <div class="print-sig-line"></div>
              <div class="print-sig-label">Approved By</div>
            </div>
          </div>
          <div class="print-footer-text" style="margin-top: 20px;">
            Generated by Gismac Security Management System | www.secs.gismac.co.ke
          </div>
        </div>
      </div>
    </body>
    </html>
  `

  const handlePrint = () => {
    const w = window.open('', '_blank')
    w.document.write(printContent)
    w.document.close()
    setTimeout(() => w.print(), 500)
  }

  return (
    <div className="print-modal-overlay">
      <div className="print-modal">
        <div className="print-modal-header">
          <h3>Budget Preview</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="print-modal-body">
          <iframe 
            srcDoc={printContent} 
            style={{ width: '100%', height: '600px', border: 'none' }}
            title="Budget Preview"
          />
        </div>
        <div className="print-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
          <button className="btn-primary" onClick={handlePrint}>
            <Printer size={16} /> Print
          </button>
        </div>
      </div>
    </div>
  )
}

export default PrintBudget
